import { useEffect, useState } from 'react';

import { Box } from '@mui/material';
import BackButton from '@apigrowfinance/pbolt-back-button';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { Outlet, useNavigate } from 'react-router-dom';
import StickyBox from 'react-sticky-box';

import AccountTabs from '@components/accountTabs/AccountTabs';
import LoadingError from '@components/loadingError/LoadingError';
import OperatorSessionContainer from '@components/operatorSession/OperatorSessionContainer';
import { useAccount } from '@contexts/AccountProvider';
import AccountContainerHeader from '@pages/accountContainer/AccountContainerHeader';
import AccountStatus from '@pages/accountContainer/AccountStatus';
import DetailsPageHeader from '@pages/accountDetail/components/pageHeader/DetailsPageHeader';
import getEnumTranslation from '@utils/getEnumTranslation';

export enum HeaderType {
    None = 'None',
    ShortSticky = 'shortSticky',
    Full = 'Full',
}

type AccountLayoutProps = {
    headerType: HeaderType;
};

const AccountLayout = ({ headerType }: AccountLayoutProps) => {
    const { account, isLoading, error } = useAccount();
    const [scrollPosition, setScrollPosition] = useState(0);
    const { theme } = useTheme();
    const navigate = useNavigate();

    const handleScroll = () => {
        const position = window.pageYOffset;
        setScrollPosition(position);
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [scrollPosition]);

    const handleGoBack = () => {
        navigate(-1);
    };

    if (error?.accountIsError || error?.programIsError) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    width: '100%',
                    height: 'calc(100vh - 88px)',
                }}>
                <LoadingError />
            </Box>
        );
    }

    return (
        <>
            {headerType === HeaderType.Full && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            height: (theme) => theme.spacing(7.5),
                        }}>
                        <BackButton
                            text={getEnumTranslation('back')}
                            iconSize={theme.spacing(2.75)}
                            buttonOptions={{
                                sx: {
                                    textTransform: 'none',
                                    p: 0,
                                    justifyContent: 'flex-start',
                                },
                            }}
                            textOptions={{ variant: 'overline' }}
                            onClick={handleGoBack}
                        />
                        <AccountStatus />
                    </Box>
                    <DetailsPageHeader
                        isLoading={isLoading}
                        name={account?.name}
                        email={account?.email}
                    />
                </>
            )}

            {headerType === HeaderType.None && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: (theme) => theme.spacing(7.5),
                    }}>
                    <BackButton
                        text={getEnumTranslation('back')}
                        iconSize={theme.spacing(2.75)}
                        buttonOptions={{
                            sx: {
                                textTransform: 'none',
                                p: 0,
                                justifyContent: 'flex-start',
                            },
                        }}
                        textOptions={{ variant: 'overline' }}
                        onClick={handleGoBack}
                    />
                </Box>
            )}

            {headerType === HeaderType.ShortSticky && (
                <StickyBox
                    offsetTop={88}
                    style={{
                        backgroundColor: theme?.palette?.background?.default,
                        zIndex: '1',
                        boxShadow:
                            scrollPosition > 20
                                ? '0 15px 15px -15px gray'
                                : 'none',
                        transition: 'box-shadow 0.3s ease-in-out',
                    }}>
                    <AccountContainerHeader
                        isLoading={isLoading}
                        name={account?.name}
                        email={account?.email}
                    />
                    <AccountTabs />
                </StickyBox>
            )}

            <Box
                sx={{
                    pt: headerType === 'shortSticky' ? 2 : 0,
                    marginBottom: 2,
                }}>
                <Outlet />
                <OperatorSessionContainer />
            </Box>
        </>
    );
};

export default AccountLayout;
